import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Pin } from 'lucide-react';
import {FiTruck, FiUserCheck} from "react-icons/fi";
import { useSupplierProcessingStore } from "../../../store/useSupplierProcessingStore.ts";
import Button from "../../Button";
import { useToggleProcessingShipmentPin } from "../../../features/supplier-fulfillment/hooks/useToggleProcessingShipmentPin";

const ShipmentCard = ({ shipment }) => {
  const shippingLabels = {
    carrier: { label: "ارسال شرکت حمل", icon: <FiTruck className="w-5 h-5" /> },
    supplier: { label: "ارسال تأمین‌کننده", icon: <FiUserCheck className="w-5 h-5" /> },
    pickup: { label: "تحویل حضوری", icon: <FiUserCheck className="w-5 h-5" /> },
  };

  const openShipmentModal = useSupplierProcessingStore((s)=>s.openShipmentModal)
  const togglePinMutation = useToggleProcessingShipmentPin();

  // تابعی برای تبدیل وضعیت به متن فارسی و رنگ مناسب
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'PENDING_APPROVING':
        return { text: 'در انتظار تایید', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' };
      case 'PENDING_PROCESSING':
        return { text: 'در انتظار آماده سازی', color: 'text-blue-600 bg-blue-50 border-blue-200' };
      case 'READY_FOR_HANDOFF':
        return { text: 'در انتظار حمل', color: 'text-green-600 bg-green-50 border-green-200' };
      case 'IN_TRANSIT':
        return { text: 'در مسیر تحویل', color: 'text-Blue-700 bg-green-50 border-green-200' };
      default:
        return { text: status, color: 'text-gray-600 bg-gray-50 border-gray-200' };
    }
  };

  const handlePinChange = (e) => {
    const checked = e.target.checked;

    togglePinMutation.mutate({
      shipmentCode: shipment.shipment_code,
      isPinned: checked,
    });
  };

  const status = getStatusDisplay(shipment.status);

  return (
      <div className="bg-white border rounded-xl p-6 shadow-sm mb-4 relative overflow-hidden text-right" dir="rtl">

        {/* هدر کارت: وضعیت و کد محموله */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">کد محموله</span>
            <span className="font-bold text-gray-800">{shipment.shipment_code.substring(0, 8)}</span>
          </div>
          <div>
            {shipment.delay_days > 0 && (
                <div
                    className="flex items-center gap-1 bg-red-600 text-white rounded-[8px] py-[4px] px-[8px] text-[14px]">
            <span>
              {shipment.delay_days}
            </span>
                  <span> روز تأخیر</span>
                </div>
            )}
          </div>

          <div className={`px-4 py-1 rounded-full border text-sm font-medium ${status.color}`}>
            {status.text}
          </div>
        </div>

        {/* بخش اصلی اطلاعات (Grid 3 تایی مشابه طرح) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-12 text-sm">

          {/* ستون اول */}
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-500">کد سفارش</span>
              <span className="font-semibold">{shipment.order_code.substring(0, 8)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">نام مشتری</span>
              <span className="font-semibold">{shipment.receiver_full_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">تعداد اقلام</span>
              <span className="font-semibold">{shipment.items_count}</span>
            </div>
          </div>

          {/* ستون دوم */}
          <div className="space-y-4">
            <div className="flex justify-between border-r md:pr-6 border-gray-100">
              <span className="text-gray-500">زمان ثبت</span>
              <span className="font-semibold">
                {new Date(shipment.created_at).toLocaleDateString('fa-IR')}
            </span>
            </div>
            <div className="flex justify-between border-r md:pr-6 border-gray-100">
              <span className="text-gray-500">تلفن مشتری</span>
              <span className="font-semibold">{shipment.receiver_phone}</span>
            </div>
            <div className="flex justify-between border-r md:pr-6 border-gray-100">
              <span className="text-gray-500">ارزش کل (تومان)</span>
              <span className="font-semibold">
                {Number(shipment.total_amount).toLocaleString()}
            </span>
            </div>
          </div>

          {/* ستون سوم */}
          <div className="space-y-4">
            <div className="flex justify-between border-r md:pr-6 border-gray-100">
              <span className="text-gray-500">استان-شهر</span>
              <span className="font-semibold">{shipment.destination}</span>
            </div>
            <div className="flex justify-between border-r md:pr-6 border-gray-100">
              <span className="text-gray-500">روش ارسال</span>
              <span className="font-semibold">{shippingLabels[shipment.delivery_method_title].label}</span>
            </div>
            <div className="flex justify-between border-r md:pr-6 border-gray-100">
              <span className="text-gray-500">وزن کل (kg)</span>
              <span className="font-semibold">{shipment.total_weight} </span>
            </div>
          </div>

        </div>

        {/* بخش پایین: تصاویر و دکمه عملیات */}
        <div className="mt-8 pt-6 border-t border-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">

          <div className="flex gap-2">
            {shipment.items.map((product, index) => (
                <Link
                    key={index}
                    to={`/supplier-panel/supplier-product/${product.product_id}`}
                    className="relative w-16 h-16 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:border-blue-300 transition-colors flex-shrink-0"
                >
                  <div
                      className="w-16 h-16 bg-blue-50 rounded-lg border border-blue-100 flex items-center justify-center overflow-hidden">
                    <img
                        src={product.product_image}
                        alt={product.product_name}
                        className="w-full h-full object-cover"
                    />
                  </div>
                </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <label
                className="
        flex cursor-pointer select-none items-center gap-2 rounded-lg border
        border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700
        transition hover:bg-gray-100
      "
            >
              <input
                  type="checkbox"
                  checked={Boolean(shipment.is_pinned_by_supplier)}
                  onChange={handlePinChange}
                  disabled={togglePinMutation.isPending || shipment.status === "DELIVERED"}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />


              <span>
        سنجاق کردن
      </span>
            </label>

            <Button
                variant={"primary"}
                onClick={() => openShipmentModal(shipment.shipment_code)}
                children={"عملیات محموله"}
                icon={<Box size={18} className="text-yellow-400"/>}
            />
          </div>
        </div>

      </div>
  );
};

export default ShipmentCard;
